import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 flex items-start gap-3",
  {
    variants: {
      variant: {
        info: "bg-background text-foreground border-border",
        success:
          "bg-green-50 text-green-900 border-green-200 [&>svg]:text-green-600",
        destructive: "border-red-600/50 text-red-600 [&>svg]:text-red-600",
      },
      size: {
        default: "text-sm",
        sm: "text-[10px]",
      },
    },
    defaultVariants: {
      variant: "info",
      size: "default",
    },
  },
);

// Context to pass size to children
const AlertSizeContext = React.createContext<"default" | "sm">("default");

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  autoDismiss?: boolean;
  onDismiss?: () => void;
  dismissAfter?: number;
  size?: "default" | "sm";
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant,
      size = "default",
      autoDismiss,
      onDismiss,
      dismissAfter = 10000,
      children,
      ...props
    },
    ref,
  ) => {
    React.useEffect(() => {
      if (autoDismiss && onDismiss) {
        const timer = setTimeout(() => {
          onDismiss();
        }, dismissAfter);
        return () => clearTimeout(timer);
      }
    }, [autoDismiss, onDismiss, dismissAfter]);

    return (
      <AlertSizeContext.Provider value={size}>
        <div
          ref={ref}
          role="alert"
          className={cn(alertVariants({ variant, size }), className)}
          {...props}
        >
          {children}
        </div>
      </AlertSizeContext.Provider>
    );
  },
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { size?: "default" | "sm" }
>(({ className, size, ...props }, ref) => {
  const contextSize = React.useContext(AlertSizeContext);
  const appliedSize = size || contextSize || "default";
  return (
    <div
      ref={ref}
      className={cn(
        appliedSize === "sm" ? "text-[10px]" : "text-sm",
        "[&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
});
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
